import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold ring-offset-background transition-all duration-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground rounded-xl shadow-button hover:shadow-button-hover hover:scale-[1.02] active:scale-[0.98]",
        destructive: "bg-destructive text-destructive-foreground rounded-xl hover:bg-destructive/90",
        outline: "bg-card text-foreground border border-border/60 rounded-xl hover:bg-muted hover:border-primary/30",
        secondary: "bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80",
        ghost: "hover:bg-muted hover:text-foreground rounded-lg",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-primary text-primary-foreground rounded-xl shadow-button hover:shadow-button-hover hover:scale-[1.02] active:scale-[0.98] font-semibold",
        "hero-outline": "bg-card text-foreground border border-border/60 rounded-xl hover:bg-muted hover:border-primary/30 font-semibold",
        "dark-primary": "bg-primary text-primary-foreground rounded-xl shadow-button hover:shadow-button-hover hover:scale-[1.02] active:scale-[0.98] font-semibold",
        "dark-outline": "bg-transparent text-foreground-dark border border-foreground-dark/30 rounded-xl hover:bg-foreground-dark/10 hover:border-foreground-dark/50",
      },
      size: {
        default: "h-[52px] md:h-11 px-6 py-3 md:py-2.5 text-[1.0625rem] md:text-sm",
        sm: "h-11 md:h-9 px-5 md:px-4 text-base md:text-sm",
        lg: "h-14 md:h-12 px-8 text-[1.0625rem] md:text-base",
        xl: "h-[60px] md:h-14 px-10 text-lg md:text-base",
        header: "h-[52px] md:h-[48px] px-6 py-3 text-base md:text-[15px]",
        icon: "h-12 w-12 md:h-10 md:w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };